import { useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { Star, Users, Clock, BadgeCheck, MessageCircle } from "lucide-react";
import { SITE, IMAGES } from "../config/site";

const LINES = [
  "Luxury Hair, Beauty &",
  "Spa Experience in",
  "Annapoorneshwari Nagar",
];

const BADGES = [
  { icon: Star, label: "4.9 Google Rating", testid: "badge-rating" },
  { icon: Users, label: "201+ Happy Customers", testid: "badge-customers" },
  { icon: Clock, label: "Open Daily", testid: "badge-open" },
  { icon: BadgeCheck, label: "Professional Staff", testid: "badge-staff" },
];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [imgSrc, setImgSrc] = useState(IMAGES.hero);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-brand-deep"
    >
      <motion.img
        src={imgSrc}
        onError={() => setImgSrc(IMAGES.heroFallback)}
        alt="Luxurious Indian hair salon interior at Beauty Trendz Unisex Salon, Annapoorneshwari Nagar"
        className="absolute inset-0 w-full h-full object-cover img-warm"
        style={reduce ? undefined : { y, scale }}
        fetchpriority="high"
      />
      <div className="absolute inset-0 hero-vignette" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-40 pb-10 sm:pb-14">
        <div className="max-w-2xl rounded-[2rem] border border-white/10 bg-brand-deep/60 backdrop-blur-md px-6 py-8 sm:px-10 sm:py-12 hero-text-shadow">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="text-brand-goldlight uppercase text-xs sm:text-sm tracking-[0.3em] font-semibold mb-6"
        >
          Beauty Trendz · Unisex Salon
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif font-bold text-white leading-[1.04] tracking-tight text-[2.6rem] sm:text-6xl lg:text-7xl max-w-4xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 1,
                  delay: 0.3 + i * 0.14,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {i === 2 ? (
                  <span className="italic gold-text-gradient">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-6 max-w-xl text-white/80 text-base sm:text-lg leading-relaxed"
        >
          Professional hair styling, beauty treatments and relaxing spa
          services in a clean, welcoming and hygienic environment.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-button"
            onClick={onBook}
            className="rounded-full bg-brand-gold px-8 py-4 text-sm sm:text-base font-semibold text-white shadow-gold transition-all duration-300 hover:bg-brand-cream hover:text-brand-green hover:scale-[1.03]"
          >
            Book Appointment
          </button>
          <a
            data-testid="hero-whatsapp-button"
            href={SITE.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/40 px-8 py-4 text-sm sm:text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white hover:text-brand-green hover:scale-[1.03]"
          >
            <MessageCircle size={18} aria-hidden="true" />
            WhatsApp Us
          </a>
        </motion.div>
        </div>

        <motion.div
          data-testid="hero-trust-badges"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl hero-text-shadow"
        >
          {BADGES.map(({ icon: Icon, label, testid }) => (
            <div
              key={label}
              data-testid={testid}
              className="flex items-center gap-2.5 rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md px-4 py-3"
            >
              <Icon size={18} className="text-brand-goldlight shrink-0" aria-hidden="true" />
              <span className="text-white/90 text-xs sm:text-sm font-medium leading-tight">
                {label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
